import { DataSearchLocation, OdinDataSender } from '@myosh/odin-components';
import type {
  DropDownResult,
  DynamicFieldComponentProps,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import React, { useCallback, useMemo } from 'react';
import type { Control } from 'react-hook-form';
import OdinDropDown from '../custom-drop-downs/odin-drop-down.component';
import usePersonFieldOptions from './use-person-field-options';
import PersonSubFieldComponent from './person-sub-field.component';
import type { LookupTypeFieldProps, PersonFieldSubFieldsProps } from '../../../@types/forms';

const TEXT_FIELD = 'text';
const VALUE_FIELD = 'value';

export interface PersonFieldProps
  extends DynamicFieldComponentProps<DropDownResult>,
    LookupTypeFieldProps,
    PersonFieldSubFieldsProps {
  control: Control;
  shouldUseCreatePortal?: boolean;
}

export default function PersonField({
  id,
  control,
  label,
  value,
  onChange,
  readOnly,
  required,
  error,
  inputRef,
  shouldUseCreatePortal = false,
  lookupType,
  lookupKeyId,
  lookupHierarchyId,
  showPersonsOccupations = false,
  showPersonsManager = false,
}: PersonFieldProps) {
  const { setOptions, onSelectorVisible, subscriberRef, dataRequestedRef, dropDownRef } = usePersonFieldOptions(
    TEXT_FIELD,
    id,
    control,
    lookupType,
    lookupKeyId,
    lookupHierarchyId
  );

  const getOptionsData = useMemo(
    () => ({
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        dataRequestedRef.current = true;
        subscriberRef.current = subscriber;
        setOptions(options);
      },
    }),
    []
  );

  const onValueChange = useCallback(
    (fieldValue: DropDownResult) => {
      if (onChange && !!fieldValue) {
        onChange(fieldValue);
      }
    },
    [onChange]
  );

  return (
    <>
      <OdinDropDown
        ref={dropDownRef}
        label={label}
        textField={TEXT_FIELD}
        valueField={VALUE_FIELD}
        value={value}
        onChange={(value) => onValueChange(value as DropDownResult)}
        readOnly={readOnly}
        required={required}
        allowSearch={true}
        shouldUseCreatePortal={shouldUseCreatePortal}
        filterLocation={DataSearchLocation.Api}
        error={error}
        data={getOptionsData}
        inputRef={inputRef}
        deselectValue={undefined}
        allowDuplicateCall
        onSelectorVisible={onSelectorVisible}
      />
      <PersonSubFieldComponent
        person={value}
        showPersonsOccupations={showPersonsOccupations}
        showPersonsManager={showPersonsManager}
        readOnly={readOnly}
      />
    </>
  );
}
